// Extra CMS slots for the 'about' page — added to make previously-hardcoded
// public marketing copy editable. Concatenated into CMS_SLOTS via ./index.
// Each default MUST equal the exact copy the component rendered before wiring,
// so the public page is byte-identical until an admin edits it.
import type { SlotDef } from '../slots';

export const aboutExtra: SlotDef[] = [
  // ─── Section headings (eyebrow / title / lede) ─────────────────────────
  // Every section header on the page is CMS-editable so the whole page can be
  // reworded without a deploy. Mirrors the what-we-are-and-aren't pattern.
  {
    key: 'about.origin_eyebrow',
    page: 'about',
    kind: 'markdown',
    label: 'Origin: eyebrow',
    default: 'The short of it',
  },
  {
    key: 'about.origin_title',
    page: 'about',
    kind: 'markdown',
    label: 'Origin: title',
    default: 'Where it came from.',
  },
  {
    key: 'about.luang_eyebrow',
    page: 'about',
    kind: 'markdown',
    label: 'Luang Prabang: eyebrow',
    default: 'Luang Prabang',
  },
  {
    key: 'about.luang_title',
    page: 'about',
    kind: 'markdown',
    label: 'Luang Prabang: title',
    default: 'The exchange, working as it should.',
  },
  {
    key: 'about.why_eyebrow',
    page: 'about',
    kind: 'markdown',
    label: 'Why this exists: eyebrow',
    default: 'The why',
  },
  {
    key: 'about.why_title',
    page: 'about',
    kind: 'markdown',
    label: 'Why this exists: title',
    default: 'Why this exists.',
  },
  // ─── In-body photos, one per paragraph (added 2026-08-02, alternating-side
  // layout added 2026-08-24 at Richie's request: "it's too text heavy") ────
  // Photo 1 pairs with this section's first paragraph, photo 2 with the
  // second, and so on, floated to alternating sides (photo 1 right, photo 2
  // left, ...) so the section reads as text-and-image, not a wall of type.
  // Every default is EMPTY, so the page is byte-identical until he uploads —
  // an empty list renders nothing at all rather than leaving a gap. Any
  // photos beyond the paragraph count fall back to a strip below the prose.
  // These carry real alt text: unlike the decorative homepage marquee, these are
  // content photographs a reader is meant to look at.
  {
    key: 'about.origin_images',
    page: 'about',
    kind: 'image_list',
    label: 'Where it came from: photos',
    helpText:
      'Optional photos, one per paragraph in order. Photo 1 sits beside the first paragraph on the right, photo 2 beside the second on the left, and so on. Any extra photos show in a strip below. Describe each photo in its alt text.',
    default: [],
  },
  {
    key: 'about.luang_images',
    page: 'about',
    kind: 'image_list',
    label: 'Luang Prabang: photos',
    helpText:
      'Optional photos, one per paragraph in order. Photo 1 sits beside the first paragraph on the right, photo 2 beside the second on the left, and so on. Any extra photos show in a strip below. Describe each photo in its alt text.',
    default: [],
  },
  {
    key: 'about.why_images',
    page: 'about',
    kind: 'image_list',
    label: 'Why this exists: photos',
    helpText:
      'Optional photos, one per paragraph in order. Photo 1 sits beside the first paragraph on the right, photo 2 beside the second on the left, and so on. Any extra photos show in a strip below. Describe each photo in its alt text.',
    default: [],
  },
  {
    key: 'about.promise_eyebrow',
    page: 'about',
    kind: 'markdown',
    label: 'Promise: eyebrow',
    default: 'The promise',
    deprecated: true,
  },
  {
    key: 'about.promise_title',
    page: 'about',
    kind: 'markdown',
    label: 'Promise: title',
    default: 'What we promise the community.',
    deprecated: true,
  },
  {
    key: 'about.promise_lede',
    page: 'about',
    kind: 'markdown',
    label: 'Promise: lede',
    default:
      "Three commitments that don't move. Written here so they can be read back at us if they ever do.",
    deprecated: true,
  },
  {
    key: 'about.contact_eyebrow',
    page: 'about',
    kind: 'markdown',
    label: 'Get in touch: eyebrow',
    default: 'Get in touch',
    deprecated: true,
  },
  {
    key: 'about.contact_title',
    page: 'about',
    kind: 'markdown',
    label: 'Get in touch: title',
    default: 'Where to find us.',
    deprecated: true,
  },

  // ─── Pull-quote callouts ───────────────────────────────────────────────
  {
    key: 'about.origin_quote',
    page: 'about',
    kind: 'markdown',
    label: 'Origin: pull quote',
    helpText: 'The large italic callout under the origin story.',
    default: 'Creativity is how people find each other.',
  },
  {
    key: 'about.luang_prabang_quote',
    page: 'about',
    kind: 'markdown',
    label: 'Luang Prabang: pull quote',
    helpText: 'The large italic callout under the Luang Prabang story.',
    default: "It worked on farms. Why couldn't it work for creatives?",
  },

  // ─── Ripple card signature ─────────────────────────────────────────────
  {
    // Retired 2026-07-11 (Richie: "REMOVE THIS LINE"). The ripple-card sign-off no
    // longer renders; slot kept (append-only) but hidden from the CMS.
    key: 'about.ripple_signature',
    page: 'about',
    kind: 'markdown',
    label: 'Ripple card: signature',
    helpText: 'The sign-off under the ripple card.',
    default: 'Richie',
    deprecated: true,
  },

  // ─── Get in touch: contact-card facts ──────────────────────────────────
  // The link destinations (mailto:, https://) stay fixed in code — editing
  // here changes the visible text only, not where a link points.
  {
    key: 'about.contact_email',
    page: 'about',
    kind: 'markdown',
    label: 'Contact card: email',
    helpText: 'Visible email text. The mailto: link itself is fixed in code.',
    default: 'hello@wwaave.com',
    deprecated: true,
  },
  {
    key: 'about.contact_web',
    page: 'about',
    kind: 'markdown',
    label: 'Contact card: website',
    helpText: 'Visible website text. The link itself is fixed in code.',
    default: 'wwaave.com',
    deprecated: true,
  },
  {
    key: 'about.contact_social',
    page: 'about',
    kind: 'markdown',
    label: 'Contact card: social handle',
    helpText: 'Visible Instagram handle. The link itself is fixed in code.',
    default: '@wwaavehello',
    deprecated: true,
  },
  {
    key: 'about.contact_legal',
    page: 'about',
    kind: 'markdown',
    label: 'Contact card: legal / location line',
    default: 'WWAAVE PTY LTD · ACN 689 639 423 · Sunshine Coast, Queensland.',
    deprecated: true,
  },

  // ─── "Read on" closing tiles ───────────────────────────────────────────
  // Block heading + each tile's copy is editable; every tile's destination
  // link is fixed in code and maps to a specific copy slot by position.
  {
    key: 'about.next_steps_title',
    page: 'about',
    kind: 'markdown',
    label: 'Read on: title',
    default: 'Read on.',
  },
  {
    key: 'about.next_steps_lede',
    page: 'about',
    kind: 'markdown',
    label: 'Read on: lede',
    default: 'The story is the why. The standards and the road notes are the everyday.',
    // Closing-block subheading removed site-wide at Richie's request — no longer
    // rendered. Retired (not deleted) so any override doesn't orphan; drops out
    // of the admin CMS editor.
    deprecated: true,
  },
  {
    key: 'about.next_steps_tile1_title',
    page: 'about',
    kind: 'markdown',
    label: 'Read on: tile 1 title',
    helpText: 'Links to Community guidelines (fixed).',
    default: 'How we hold the line',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'about.next_steps_tile1_desc',
    page: 'about',
    kind: 'markdown',
    label: 'Read on: tile 1 description',
    default: 'Community standards and how we keep the network honest.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'about.next_steps_tile2_title',
    page: 'about',
    kind: 'markdown',
    label: 'Read on: tile 2 title',
    helpText: 'Links to What we are and aren’t (fixed).',
    default: 'The WWAAVE Promise',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'about.next_steps_tile2_desc',
    page: 'about',
    kind: 'markdown',
    label: 'Read on: tile 2 description',
    default: 'What WWAAVE is and isn’t, and the principles behind every decision.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'about.next_steps_tile3_title',
    page: 'about',
    kind: 'markdown',
    label: 'Read on: tile 3 title',
    helpText: 'Links to Notes from the road (fixed).',
    default: 'Notes from the road',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'about.next_steps_tile3_desc',
    page: 'about',
    kind: 'markdown',
    label: 'Read on: tile 3 description',
    default: "Quarterly letters from Richie about what's working and what isn't.",
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },

  // ─── Closing CTA (under the ripple card) ───────────────────────────────
  // The link destinations (/sign-up?role=artist|host) are fixed in code;
  // editing here changes the visible button label only.
  {
    key: 'about.cta_artist',
    page: 'about',
    kind: 'markdown',
    label: 'Closing CTA: artist button',
    helpText: 'Label on the artist sign-up button under the ripple card.',
    default: "I'm an artist",
  },
  {
    key: 'about.cta_host',
    page: 'about',
    kind: 'markdown',
    label: 'Closing CTA: host button',
    helpText: 'Label on the host sign-up button under the ripple card.',
    default: 'I host a venue',
  },
];
